import type { PanelSend } from '@/features/assistant/usePanelSend.js';
import { fetchReportBundle, fetchRunDetail } from '@/lib/api/quality.js';
import type { CaseOutcome } from '@/lib/api/quality-types.js';
import { validateAgentActionHandoff } from './agentActionHandoff.js';
import {
  buildRunDigest,
  buildExploratoryRunDigest,
  analyzePrompt,
  exploratoryAnalyzePrompt,
  createTaskPrompt,
  exploratoryCreateTaskPrompt,
  prepareActionPrompt,
} from './runDigest.js';
import type { RunActionMode } from './runCardRef.js';

/** Revalidate selected cases/revision, then send only into the run's shared journal. */
export async function sendRunAction(
  runId: string,
  mode: RunActionMode,
  send: PanelSend,
  selection?: {
    caseIds: string[];
    outcomes: CaseOutcome[];
    reportRevision: number;
  },
): Promise<void> {
  const detail = await fetchRunDetail(runId);
  const expected = selection?.reportRevision ?? detail.run.report_revision;
  const bundle = await fetchReportBundle(runId, expected);
  if (
    detail.run.outcome_protocol_version === 1 &&
    (bundle?.coherence !== 'coherent' || bundle.reportRevision !== expected)
  )
    throw new Error(
      'The current report changed. Refresh the run and retry this action.',
    );
  if (mode !== 'analyze' && mode !== 'create-task') {
    if (!selection)
      throw new Error(
        'Select at least one current case before preparing this action.',
      );
    const problem = validateAgentActionHandoff(
      { ...selection, action: mode },
      detail,
      bundle,
    );
    if (problem) throw new Error(problem);
  }
  const exploratory = detail.run.run_kind === 'exploratory';
  const digest = exploratory
    ? buildExploratoryRunDigest(detail, bundle)
    : buildRunDigest(detail, bundle);
  const prompt =
    mode === 'analyze'
      ? exploratory
        ? exploratoryAnalyzePrompt(digest, runId)
        : analyzePrompt(digest, runId)
      : mode === 'create-task'
        ? exploratory
          ? exploratoryCreateTaskPrompt(digest, runId)
          : createTaskPrompt(digest, runId)
        : prepareActionPrompt(digest, { action: mode, runId, ...selection! });
  if (!(await send(prompt)))
    throw new Error(
      'The run assistant did not accept this action. Try again after its current reply finishes.',
    );
}
